import React, { useState, useEffect } from 'react';
import { Plus, Monitor, X, Check, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ user_id: '', mac_address: '', device_type: 'android', device_name: '' });
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [xtreamResult, setXtreamResult] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dev, usr] = await Promise.all([api.get('/api/devices'), api.get('/api/users')]);
      setDevices(dev.data.data || []);
      setUsers((usr.data.data || []).filter(u => u.role === 'enduser'));
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload = { ...form };
      if (isNewUser) {
        if (!newUserName.trim()) { toast.error('Please enter the customer name'); return; }
        const macSuffix = form.mac_address.replace(/[^A-Fa-f0-9]/g, '').toLowerCase().slice(-6) || 'user';
        const userRes = await api.post('/api/users', {
          username: `user_${macSuffix}_${Date.now().toString().slice(-4)}`,
          password: Math.random().toString(36).slice(-8),
          full_name: newUserName.trim(),
          phone: newUserPhone,
          role: 'enduser',
        });
        payload.user_id = userRes.data.data.id;
      }
      const res = await api.post('/api/devices', payload);
      toast.success('Device registered');
      setShowModal(false);
      setForm({ user_id: '', mac_address: '', device_type: 'android', device_name: '' });
      setIsNewUser(false); setNewUserName(''); setNewUserPhone('');
      if (res.data.xtream) setXtreamResult(res.data.xtream);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const handleToggle = async (id) => {
    try {
      await api.put(`/api/devices/${id}/toggle`);
      toast.success('Updated');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this device?')) return;
    try { await api.delete(`/api/devices/${id}`); toast.success('Removed'); fetchAll(); }
    catch (err) { toast.error('Failed'); }
  };

  const deviceTypeColors = { android: '#4f6ef7', androidtv: '#a78bfa', tizen: '#00d4aa', webos: '#f7a24f', macos: '#f7d44f' };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Devices</div>
          <div className="page-subtitle">{devices.length} registered devices</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Register Device
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>MAC Address</th>
                <th>Device Name</th>
                <th>Type</th>
                <th>User</th>
                <th>Registered</th>
                <th>Last Seen</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map(d => (
                <tr key={d.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: '600' }}>{d.mac_address}</td>
                  <td>{d.device_name || '—'}</td>
                  <td>
                    <span className="badge" style={{ background: `${deviceTypeColors[d.device_type] || '#4f6ef7'}20`, color: deviceTypeColors[d.device_type] || '#4f6ef7' }}>
                      {d.device_type?.toUpperCase()}
                    </span>
                  </td>
                  <td>{d.full_name || d.username || '—'}</td>
                  <td style={{ fontSize: '13px' }}>{d.registered_at ? new Date(d.registered_at).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ fontSize: '13px' }}>{d.last_seen ? new Date(d.last_seen).toLocaleDateString('en-IN') : 'Never'}</td>
                  <td><span className={`badge ${d.is_active ? 'badge-success' : 'badge-danger'}`}>{d.is_active ? 'Active' : 'Blocked'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleToggle(d.id)} className="btn btn-secondary" style={{ padding: '6px 10px' }}>
                        {d.is_active ? <ToggleRight size={14} color="var(--accent-teal)" /> : <ToggleLeft size={14} />}
                      </button>
                      <button onClick={() => handleDelete(d.id)} className="btn btn-danger" style={{ padding: '6px 10px' }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Register Device</div>
              <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>End User</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button type="button" onClick={() => setIsNewUser(false)} className="btn" style={{ flex: 1, background: !isNewUser ? 'var(--accent-blue)' : 'var(--bg-card)', color: !isNewUser ? 'white' : 'var(--text-secondary)', border: '1px solid var(--border)', padding: '8px' }}>Select Existing</button>
                  <button type="button" onClick={() => setIsNewUser(true)} className="btn" style={{ flex: 1, background: isNewUser ? 'var(--accent-blue)' : 'var(--bg-card)', color: isNewUser ? 'white' : 'var(--text-secondary)', border: '1px solid var(--border)', padding: '8px' }}>Create New</button>
                </div>
                {isNewUser ? (
                  <>
                    <input className="form-control" placeholder="Customer's full name *" required value={newUserName} onChange={e => setNewUserName(e.target.value)} style={{ marginBottom: '10px' }} />
                    <input className="form-control" placeholder="Phone (optional)" value={newUserPhone} onChange={e => setNewUserPhone(e.target.value)} />
                  </>
                ) : (
                  <select className="form-control" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})} required>
                    <option value="">Select user...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                  </select>
                )}
              </div>
              <div className="form-group">
                <label>MAC Address *</label>
                <input className="form-control" value={form.mac_address} onChange={e => setForm({...form, mac_address: e.target.value})} required placeholder="AA:BB:CC:DD:EE:FF" />
              </div>
              <div className="form-group">
                <label>Device Type</label>
                <select className="form-control" value={form.device_type} onChange={e => setForm({...form, device_type: e.target.value})}>
                  <option value="android">Android Phone</option>
                  <option value="androidtv">Android TV</option>
                  <option value="tizen">Samsung (Tizen)</option>
                  <option value="webos">LG (WebOS)</option>
                  <option value="macos">macOS</option>
                </select>
              </div>
              <div className="form-group">
                <label>Device Name</label>
                <input className="form-control" value={form.device_name} onChange={e => setForm({...form, device_name: e.target.value})} placeholder="e.g. Living Room TV" />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={16} />Register</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {xtreamResult && (
        <div className="modal-overlay" onClick={() => setXtreamResult(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">IPTV Player Login (Tizen / webOS)</div>
              <button className="close-btn" onClick={() => setXtreamResult(null)}><X size={20} /></button>
            </div>
            <div style={{ padding: '10px 0' }}>
              <p style={{ color: '#8892B0', fontSize: '13px', marginBottom: '16px' }}>
                Enter these into an IPTV player app (e.g. IPTV Smarters Pro) on this exact TV.
                This login only works on the first TV it's used on.
              </p>
              <div className="form-group">
                <label>Server URL</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input className="form-control" readOnly value="https://api.novacast.fibralink.co.in" />
                  <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard('https://api.novacast.fibralink.co.in')}>Copy</button>
                </div>
              </div>
              <div className="form-group">
                <label>Username</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input className="form-control" readOnly value={xtreamResult.xtream_username} />
                  <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard(xtreamResult.xtream_username)}>Copy</button>
                </div>
              </div>
              <div className="form-group">
                <label>Password</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input className="form-control" readOnly value={xtreamResult.xtream_password} />
                  <button type="button" className="btn btn-secondary" onClick={() => copyToClipboard(xtreamResult.xtream_password)}>Copy</button>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-primary" onClick={() => setXtreamResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;
